// Client for the three clinical-extraction endpoints (AI report parsing).
//
// Two endpoints START an extraction; the third READS BACK the latest usable
// result. All inputs are query parameters — none of them takes a body.
//
// Which reports a run covers depends on whether `documentIds` is sent:
//   - omitted  → the backend picks, from the last 24h, filtered by
//                `ai_scan_indicator` H/P
//   - supplied → exactly the reports named, of ANY age; the 24h window is not
//                consulted at all
//
// Auth is injected globally by lib/fetchInterceptor.js, so nothing here sets
// an Authorization header.
//
// See docs/report-scanner/api-clinical-extraction.md (revision 4). The older
// copies under docs/report-parser/ are revision 3 and superseded.

import C from "./Config";

export const ExtractionOutcome = {
  SUCCESS: "success", // 200 + serviceStatus SUCCESS — `data.result` is usable
  SUCCESS_STALE: "success_stale", // as SUCCESS, but a NEWER run failed (§4.1.1)
  BUSINESS: "business", // 200 + BUSINESS_VALIDATION_FAILURE — expected, show message
  SYSTEM: "system", // 500 / SYSTEM_FAILURE — generic retry
  UNAUTHORIZED: "unauthorized", // 401 — expired/blacklisted token, NOT a BaseDTO
  BAD_REQUEST: "bad_request", // 400 — malformed patientId, NOT a BaseDTO
};

export const ReportType = {
  DIAGNOSIS: "DIAGNOSIS",
  PATHOLOGY: "PATHOLOGY",
};

// Routing letter carried on every document row (and on the type master), which
// decides WHICH of the two start endpoints a report belongs to. Anything that
// is not H or P — including a null, for rows uploaded before the type master
// existed — cannot be scanned at all.
export const AiScanIndicator = {
  DIAGNOSIS: "H",
  PATHOLOGY: "P",
  NEVER: "N",
};

// Per-run limits enforced by the backend (§0). We apply them in the UI too, so
// a selection that would come back as LIMIT_REACHED / TOO_LARGE in
// `skippedDocuments` is refused before the call rather than silently dropped.
export const MAX_DOCS_PER_SCAN = 10;
export const MAX_SCAN_BYTES = 20 * 1024 * 1024;

// The extraction service accepts a NARROWER set than our upload allowlist —
// a .txt or .csv is a perfectly valid stored report that the AI cannot read.
// Match on contentType where the row has one; the extensions are the fallback
// for older rows that predate the column.
export const AI_SCANNABLE_MIME = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/tiff",
];
export const AI_SCANNABLE_EXTENSIONS = [
  "pdf",
  "png",
  "jpeg",
  "jpg",
  "webp",
  "tiff",
  // .tif is the same format as .tiff and shares the image/tiff MIME type.
  "tif",
];

// Can this report be sent for AI scanning at all, and if not, why?
//
// Answering BEFORE the call is the whole point: this screen does not poll, so
// `skippedDocuments` would never be seen. Refusing the tick up front prevents
// the two most common reasons in that catalogue — NOT_FLAGGED and
// UNSUPPORTED_FORMAT — from happening silently.
//
// `doc` is a row as built by CaseProfile's fetchPatientDocuments.
export function getScanEligibility(doc) {
  const indicator = doc && doc.aiScanIndicator;

  if (indicator !== AiScanIndicator.DIAGNOSIS && indicator !== AiScanIndicator.PATHOLOGY) {
    return {
      scannable: false,
      reason: "This report type is not set up for AI scanning.",
    };
  }

  const contentType = String((doc && doc.contentType) || "").toLowerCase().trim();
  const extension = String((doc && doc.fileName) || "")
    .toLowerCase()
    .split(".")
    .pop();

  // contentType first — it settles .tif vs .tiff without a special case. Older
  // rows stored before the column existed fall back to the extension.
  const readable = contentType
    ? AI_SCANNABLE_MIME.indexOf(contentType) !== -1
    : AI_SCANNABLE_EXTENSIONS.indexOf(extension) !== -1;

  if (!readable) {
    return {
      scannable: false,
      reason: "This file format cannot be read by the scanner (PDF or image only).",
    };
  }

  return { scannable: true, reason: null };
}

// Generic fallback when the run produced nothing and no earlier result exists.
const NOT_AVAILABLE_FALLBACK =
  'Report not available for patient. If report is uploaded in "Case Summary" tab ' +
  "then wait for sometime. If not then please upload the report and check again.";

// A SKIPPED run is reported with the generic "not available" text above, but
// the specific reason survives in `data.errorMessage`. For these two codes the
// specific text is far more actionable — telling the user to wait when nothing
// they uploaded is eligible sends them in the wrong direction. See §4.2, §6.
// NO_ELIGIBLE_DOCUMENTS is the explicit-selection counterpart of NO_DOCUMENTS:
// it means nothing the user TICKED could be used. The generic text talks about
// the 24h window, which was never consulted for a chosen selection.
const PREFER_SPECIFIC_MESSAGE = [
  "NO_DOCUMENTS",
  "NO_SUPPORTED_DOCUMENTS",
  "NO_ELIGIBLE_DOCUMENTS",
];

// Ids must be present and numeric — a malformed one is rejected by Spring with
// a 400 before the controller runs, so guard rather than send it. Used for
// patientId and for each entry of documentIds.
function isNumericId(value) {
  if (value === null || value === undefined) return false;
  const asString = String(value).trim();
  if (asString === "") return false;
  return /^\d+$/.test(asString);
}

// Builds the optional `&documentIds=881,884` suffix.
//
// An EMPTY list must produce no parameter at all: per §2 a blank `documentIds`
// is rejected with "Select at least one report to scan", whereas omitting it
// means "backend, you choose". Those are different requests and conflating
// them would silently scan documents the user did not pick.
function buildDocumentIdsParam(documentIds) {
  if (!Array.isArray(documentIds)) return "";
  const clean = documentIds.filter(isNumericId).map((id) => String(id).trim());
  // Duplicates are collapsed server-side, but sending them wastes the 50 cap.
  const unique = clean.filter((id, i) => clean.indexOf(id) === i);
  if (unique.length === 0) return "";
  return "&documentIds=" + encodeURIComponent(unique.join(","));
}

// Reads the body defensively: 401/400 come back in Spring's default error shape
// (no serviceStatus, no data), and a non-JSON body is possible on a proxy error.
async function readBody(response) {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
}

// Collapses an extraction response into { outcome, message, data }.
//
// Order matters: HTTP status is checked FIRST, because 401 and 403 do not carry
// a BaseDTO at all — reading `serviceStatus` unconditionally would throw on an
// expired session instead of sending the user to login (§1).
export function classifyExtractionResponse(httpStatus, body) {
  if (httpStatus === 401) {
    return {
      outcome: ExtractionOutcome.UNAUTHORIZED,
      message: "Your session has expired. Please sign in again.",
      data: null,
    };
  }

  if (httpStatus === 400) {
    return {
      outcome: ExtractionOutcome.BAD_REQUEST,
      message: "The request could not be completed. Please reload and try again.",
      data: null,
    };
  }

  const serviceStatus = body && body.serviceStatus;
  const data = (body && body.data) || null;

  if (serviceStatus === "SUCCESS") {
    // §4.1.1 — a SUCCESS can now carry an OLDER result, resurfaced because a
    // newer run failed. `data.extractionResultId` is then the older run's id,
    // which is the correct thing to stamp on a saved diagnosis: it identifies
    // the run the data actually came from.
    const isStale = !!(data && data.lastAttemptStatus);
    return {
      outcome: isStale
        ? ExtractionOutcome.SUCCESS_STALE
        : ExtractionOutcome.SUCCESS,
      message: (body && body.message) || null,
      data,
    };
  }

  if (serviceStatus === "BUSINESS_VALIDATION_FAILURE") {
    // All errorMsg text is curated by the backend and safe to render verbatim.
    let message = (body && body.errorMsg) || NOT_AVAILABLE_FALLBACK;
    if (
      data &&
      data.errorCode &&
      PREFER_SPECIFIC_MESSAGE.indexOf(data.errorCode) !== -1 &&
      data.errorMessage
    ) {
      message = data.errorMessage;
    }
    return { outcome: ExtractionOutcome.BUSINESS, message, data };
  }

  // SYSTEM_FAILURE, an unexpected status, or an unparseable body.
  return {
    outcome: ExtractionOutcome.SYSTEM,
    message:
      (body && body.errorMsg) ||
      "The extraction service could not be reached. Please try again.",
    data,
  };
}

async function callExtraction(path) {
  const response = await fetch(C.getUrl() + path, {
    method: "POST",
    mode: "cors",
  });
  const body = await readBody(response);
  return classifyExtractionResponse(response.status, body);
}

// POST /extraction/extractDiagnosis — queues a run over H-flagged reports.
// Returns in milliseconds; the result takes up to ~3 minutes.
//
// `documentIds` (optional array) names the reports to use, ignoring the 24h
// window. Omit it to keep the original behaviour where the backend chooses.
//
// Starting SUCCEEDS even when some ids turn out to be unusable — per-report
// validation happens on the worker and surfaces later in `skippedDocuments`.
export function startDiagnosisExtraction(patientId, documentIds) {
  if (!isNumericId(patientId)) {
    return Promise.resolve({
      outcome: ExtractionOutcome.BAD_REQUEST,
      message: "Patient is required.",
      data: null,
    });
  }
  return callExtraction(
    "/extraction/extractDiagnosis?patientId=" +
      encodeURIComponent(patientId) +
      buildDocumentIdsParam(documentIds)
  );
}

// POST /extraction/extractPathology — same contract, P-flagged reports.
// Completes in seconds rather than minutes.
export function startPathologyExtraction(patientId, documentIds) {
  if (!isNumericId(patientId)) {
    return Promise.resolve({
      outcome: ExtractionOutcome.BAD_REQUEST,
      message: "Patient is required.",
      data: null,
    });
  }
  return callExtraction(
    "/extraction/extractPathology?patientId=" +
      encodeURIComponent(patientId) +
      buildDocumentIdsParam(documentIds)
  );
}

// GET /extraction/getExtractionResult — reads the latest usable result.
// Safe to call without triggering anything. `reportType` is case-insensitive
// server-side but we send the canonical upper-case form.
export function getExtractionResult(patientId, reportType) {
  if (!isNumericId(patientId)) {
    return Promise.resolve({
      outcome: ExtractionOutcome.BAD_REQUEST,
      message: "Patient is required.",
      data: null,
    });
  }

  const type = reportType || ReportType.DIAGNOSIS;
  const url =
    C.getUrl() +
    "/extraction/getExtractionResult?patientId=" +
    encodeURIComponent(patientId) +
    "&reportType=" +
    encodeURIComponent(type);

  return fetch(url, { method: "GET", mode: "cors" })
    .then(async (response) => {
      const body = await readBody(response);
      return classifyExtractionResponse(response.status, body);
    });
}
